import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Chip,
  Stack,
  SxProps,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import { useMemo, useState } from 'react';
import { ReportImageUploadModal } from './ReportImageUploadModal';
import { SmallButton } from '@/components/UserManagement/styles';
import { useAtomValue } from 'jotai';
import * as state from '@/components/Analysis/state';
import { FileNameTypes, FileUploadStatus, ImageType } from '@/__generated__/graphql';
import { abyssColors } from '@/theme/colors';
import { ReportImagesGallery } from './ReportImagesGallery';

type DispalyLabel = 'Chase Items' | 'Uploads';

const LabelChipStyleOptions: Record<DispalyLabel, SxProps> = {
  Uploads: {
    // border: `2px solid #93C5FD`,
    color: '#1D4ED8',
  },
  'Chase Items': {
    border: `2px solid #C4B5FD`,
    color: '#6D28D9',
  },
};

const LabelChip = ({ label }: { label: DispalyLabel }) => {
  const sx = LabelChipStyleOptions[label];
  return (
    <Chip
      variant="outlined"
      label={label}
      sx={{
        fontSize: '12px',
        fontWeight: 500,
        margin: '1rem',
        ...sx,
      }}
    />
  );
};

export const ReportImagesAccordian = () => {
  const [open, setOpen] = useState(false);

  const report = useAtomValue(state.selectedReport);
  const files = useMemo(() => report?.files ?? [], [report]);
  const pointOfInterests = useMemo(() => report?.pointOfInterests ?? [], [report]);
  const poiFiles = useMemo(() => pointOfInterests?.flatMap((poi) => poi?.files ?? []), [pointOfInterests]);

  const reportImages = useMemo(
    () =>
      poiFiles?.filter(
        (file) =>
          (file?.name === FileNameTypes.PoiScreenshot ||
            file?.name === FileNameTypes.PoiAttachments) &&
          file.uploadStatus === FileUploadStatus.Success &&
          file.imageType !== ImageType.WhiteBgPdf
      ),
    [poiFiles]
  );

  const userAttachments = useMemo(
    () =>
      files?.filter(
        (file) =>
          file?.name === FileNameTypes.UserAttachments &&
          file.uploadStatus === FileUploadStatus.Success
      ),
    [files]
  );

  if (!report) return <></>;

  return (
    <div>
      <Accordion
        sx={{
          boxShadow: 'none',
          border: 'none',
        }}
        defaultExpanded
      >
        <AccordionSummary
          sx={{
            flexDirection: 'row-reverse',
          }}
          expandIcon={<ExpandMoreIcon style={{ color: abyssColors.primary[900] }} />}
        >
          <Stack
            direction={'row'}
            alignItems={'center'}
            justifyContent={'space-between'}
            width={'100%'}
          >
            <Typography fontSize={'16px'} fontWeight={500}>
              Media
            </Typography>

            <SmallButton
              onClick={(event) => {
                // To stop the accrodain from closing whenever the button is clicked
                event.stopPropagation();
                setOpen(true);
              }}
              sx={{ textTransform: 'none' }}
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
            >
              Add Images
            </SmallButton>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 2 }}>
          {Array.isArray(userAttachments) && userAttachments.length > 0 ? (
            <>
              <LabelChip label={'Uploads'} />
              <ReportImagesGallery images={userAttachments} />
            </>
          ) : (
            <></>
          )}
          {Array.isArray(reportImages) && reportImages.length > 0 ? (
            <>
              <LabelChip label={'Chase Items'} />
              <ReportImagesGallery images={reportImages} />
            </>
          ) : (
            <></>
          )}
        </AccordionDetails>
      </Accordion>
      <ReportImageUploadModal open={open} onClose={() => setOpen(false)} />
    </div>
  );
};
